// src/components/NewsCarousel.jsx
import React, { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  useTheme,
  alpha,
  Alert,
  Skeleton,
} from "@mui/material";
import { FiArrowLeft, FiArrowRight, FiArrowUpRight } from "react-icons/fi";
import useSWR from "swr";

/* ──────────── CONFIG ──────────── */
const ORANGE = "#FF8A00";
const FEED =
  "https://news.google.com/rss/search?q=paneles+solares+when:7d&hl=es-419&gl=MX&ceid=MX:es-419";

const PROXIES = [
  (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`, // 1️⃣
  (url) => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  (url) => `https://thingproxy.freeboard.io/fetch/${url}`,
];

const REFRESH_EVERY = 1_000 * 60 * 30; // 30 min
const FALLBACK_DESC = "Nota sobre energía solar reciente.";

/* —— FILTRO ——
   Cualquiera de estas coincidencias (en título o descripción) descarta la noticia */
const BLOCKED_TERMS = [
  "tinac", // tinaco / tinacos
  "marcha", // marcha, marcharán…
  "protest", // protesta, protestan…
  "contra",
  "corrup",
  "demand",
  "incendio",
  "accidente",
];

const BLOCKED_SOURCES = ["la jornada", "infobae"];

/* ─────────── FETCH & PARSE ─────────── */
async function fetchFeed() {
  let xmlText = null;

  for (const wrap of PROXIES) {
    try {
      const res = await fetch(wrap(FEED), { cache: "no-store" });
      if (!res.ok) continue;
      const txt = await res.text();
      if (!txt.includes("<item")) continue;
      xmlText = txt;
      break;
    } catch {
      /* intenta con el siguiente proxy */
    }
  }

  if (!xmlText) return []; // todos fallaron

  const doc = new DOMParser().parseFromString(xmlText, "text/xml");
  const decode = (h) =>
    new DOMParser().parseFromString(h, "text/html").documentElement.textContent;

  // ------ Construimos el array crudo ------
  const rawItems = Array.from(doc.querySelectorAll("item")).map((it) => {
    const link = it.querySelector("link")?.textContent ?? "#";
    const titleRaw = it.querySelector("title")?.textContent ?? "";
    const title = decode(titleRaw);
    const brandRaw =
      it.querySelector("source")?.textContent?.trim() ||
      new URL(link).hostname.replace("www.", "").split(".")[0] ||
      "News";
    const brand = brandRaw.charAt(0).toUpperCase() + brandRaw.slice(1);

    const descRaw =
      it.querySelector("description")?.textContent ?? FALLBACK_DESC;
    const plain = decode(descRaw.replace(/<[^>]+>/g, "")).trim();
    const desc =
      plain.length > 110 ? plain.slice(0, 107).trimEnd() + "…" : plain;

    return { brand, title, desc, url: link };
  });

  // ------ Filtro de relevancia ------
  return rawItems
    .filter(({ brand, title, desc }) => {
      const txt = (title + " " + desc).toLowerCase();
      const bySource = brand.toLowerCase();
      if (BLOCKED_SOURCES.some((s) => bySource.includes(s))) return false;
      if (BLOCKED_TERMS.some((t) => txt.includes(t))) return false;
      return true;
    })
    .slice(0, 10); // máximo 10 artículos
}

/* ─────────── HOOK SWR ─────────── */
const useSolarNews = () =>
  useSWR("solarNews", fetchFeed, {
    refreshInterval: REFRESH_EVERY,
    revalidateOnFocus: false,
  });

/* ─────────── COMPONENTE PRINCIPAL ─────────── */
export default function NewsCarousel() {
  const { data: articles, error } = useSolarNews();
  const theme = useTheme();
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const t = trackRef.current;
    if (!t) return;
    const w = t.firstChild?.offsetWidth || 280;
    t.scrollBy({ left: dir * (w + 24), behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        background: "linear-gradient(180deg,#fff 0%,#FAFAFA 100%)",
        borderTop: "1px solid #eee",
      }}
    >
      <Container maxWidth="lg">
        {/* — Título + flechas — */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Ethos Nova, sans-serif",
              fontWeight: 800,
              position: "relative",
              pb: 0.5,
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                bottom: 0,
                width: 72,
                height: 3,
                bgcolor: ORANGE,
                borderRadius: 2,
              },
            }}
          >
            Noticias Destacadas
          </Typography>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            <IconButton
              onClick={() => scroll(-1)}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              <FiArrowLeft />
            </IconButton>
            <IconButton
              onClick={() => scroll(1)}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              <FiArrowRight />
            </IconButton>
          </Box>
        </Box>

        {/* — Estados de carga / error — */}
        {error && (
          <Alert severity="error">No se pudo cargar el feed de noticias.</Alert>
        )}

        {!articles && (
          <Box sx={{ display: "flex", gap: 3 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={330}
                width={280}
                sx={{ borderRadius: 3 }}
              />
            ))}
          </Box>
        )}

        {/* — Carrusel — */}
        {articles && (
          <Box
            ref={trackRef}
            sx={{
              display: "flex",
              gap: 3,
              overflowX: "auto",
              overflowY: "visible",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              pb: 1,
            }}
          >
            {articles.map(({ brand, desc, url }, i) => {
              const dark = i % 2 === 1; // alterna blanco / negro
              return (
                <Box
                  key={url}
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  scrollSnapAlign="start"
                  sx={{
                    flex: "0 0 280px",
                    height: 330,
                    textDecoration: "none",
                    bgcolor: dark ? "#121212" : "#fff",
                    color: dark ? "#fff" : "#000",
                    borderRadius: 3,
                    boxShadow: `0 6px 20px ${alpha("#000", 0.06)}`,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "transform .25s, box-shadow .25s",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 16px 32px ${alpha("#000", 0.14)}`,
                    },
                  }}
                >
                  {/* Marca + icono */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontWeight: 900,
                        lineHeight: 1,
                      }}
                    >
                      {brand}
                    </Typography>
                    <FiArrowUpRight />
                  </Box>

                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {desc}
                  </Typography>

                  {/* CTA */}
                  <Box
                    sx={{
                      mt: 3,
                      bgcolor: ORANGE,
                      color: "#fff",
                      borderRadius: 2,
                      py: 1,
                      px: 2,
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: "Ethos Nova, sans-serif",
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    Leer artículo
                    <FiArrowUpRight size={14} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}
